import { create } from "zustand";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  deleteDoc,
  doc,
  serverTimestamp,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject, updateMetadata } from "firebase/storage";
import { toast } from "@/hooks/use-toast";
import type { DiskFolder, DiskFile } from "@/types/disk";

interface DiskState {
  folders: DiskFolder[];
  files: DiskFile[];
  loading: boolean;
  init: (userId?: string) => void;
  createFolder: (name: string, parentPath: string, userId: string) => Promise<void>;
  uploadFile: (file: File, path: string, userId: string) => Promise<void>;
  uploadVideo: (file: File, title: string, description: string, path: string, userId: string) => Promise<void>;
  deleteItem: (id: string, type: "folder" | "file", storagePath?: string) => Promise<void>;
  togglePublic: (id: string, type: "folder" | "file", makePublic: boolean) => Promise<void>;
  cleanup: () => void;
}

let unsubscribeFolders: (() => void) | null = null;
let unsubscribeFiles: (() => void) | null = null;

const TARGET_USER_ID = "4";

export const useDiskStore = create<DiskState>((set, get) => ({
  folders: [],
  files: [],
  loading: false,

  init: (userId?: string) => {
    if (unsubscribeFolders || unsubscribeFiles) {
      console.log("[diskStore] Подписки уже активны — пропускаем");
      return;
    }

    const effectiveUserId = userId || TARGET_USER_ID;
    console.log("[diskStore] Инициализация для userId:", effectiveUserId);

    const foldersQuery = query(
      collection(db, "diskFolders"),
      where("userId", "==", effectiveUserId)
    );

    unsubscribeFolders = onSnapshot(
      foldersQuery,
      (snap) => {
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as DiskFolder));
        set({ folders: data });
      },
      (err) => {
        console.error("[diskStore] Ошибка папок:", err);
        toast({ title: "Ошибка загрузки папок", variant: "destructive" });
      }
    );

    const filesQuery = query(
      collection(db, "diskFiles"),
      where("userId", "==", effectiveUserId)
    );

    unsubscribeFiles = onSnapshot(
      filesQuery,
      (snap) => {
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as DiskFile));
        set({ files: data });
      },
      (err) => {
        console.error("[diskStore] Ошибка файлов:", err);
        toast({ title: "Ошибка загрузки файлов", variant: "destructive" });
      }
    );
  },

  createFolder: async (name: string, parentPath: string = "", userId: string) => {
    if (!userId) {
      toast({ title: "Ошибка", description: "Нет авторизации", variant: "destructive" });
      return;
    }
    if (!name.trim()) return;

    try {
      const trimmedName = name.trim();
      const fullPath = parentPath ? `${parentPath}/${trimmedName}` : trimmedName;

      const checkQuery = query(
        collection(db, "diskFolders"),
        where("userId", "==", userId),
        where("path", "==", fullPath)
      );
      const existing = await getDocs(checkQuery);
      if (!existing.empty) {
        toast({ title: "Такая папка уже есть", variant: "destructive" });
        return;
      }

      await addDoc(collection(db, "diskFolders"), {
        name: trimmedName,
        path: fullPath,
        createdAt: serverTimestamp(),
        userId,
        isPublic: false,
      });

      toast({ title: "Папка создана" });
    } catch (err: any) {
      console.error(err);
      toast({ title: "Не удалось создать папку", description: err.message, variant: "destructive" });
    }
  },

  uploadFile: async (file: File, path: string = "", userId: string) => {
    if (!userId) {
      toast({ title: "Ошибка", description: "Нет авторизации", variant: "destructive" });
      return;
    }

    try {
      set({ loading: true });
      const storagePath = `disk/${userId}/${path ? path + "/" : ""}${file.name}`;
      const fileRef = ref(storage, storagePath);

      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      await updateMetadata(fileRef, {
        customMetadata: { isPublic: "false" }
      });

      await addDoc(collection(db, "diskFiles"), {
        name: file.name,
        url,
        path: path || "",
        type: file.type,
        createdAt: serverTimestamp(),
        userId,
        isPublic: false,
      });

      toast({ title: "Файл загружен" });
    } catch (err: any) {
      console.error(err);
      toast({ title: "Ошибка загрузки", description: err.message, variant: "destructive" });
    } finally {
      set({ loading: false });
    }
  },

  uploadVideo: async (file: File, title: string, description: string, path: string, userId: string) => {
    if (userId !== TARGET_USER_ID) {
      toast({ title: "Загрузка видео доступна только для тестовой учётной записи", variant: "destructive" });
      return;
    }

    try {
      set({ loading: true });

      // Путь в Storage — такой же, как у обычных файлов
      const storagePath = `disk/${userId}/${path ? path + '/' : ''}${Date.now()}_${file.name}`;
      const fileRef = ref(storage, storagePath);

      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      // Добавляем в diskFiles — теперь видео видно в папке как обычный файл
      await addDoc(collection(db, "diskFiles"), {
        name: title,
        url,
        path,
        type: "video/mp4",
        description,
        createdAt: serverTimestamp(),
        userId,
        isPublic: true, // сразу публичное, чтобы было видно по QR
      });

      toast({ title: "Видео добавлено в папку" });
    } catch (err: any) {
      console.error(err);
      toast({ title: "Ошибка загрузки видео", description: err.message, variant: "destructive" });
    } finally {
      set({ loading: false });
    }
  },

  deleteItem: async (id: string, type: "folder" | "file", storagePath?: string) => {
    try {
      if (type === "folder") {
        await deleteDoc(doc(db, "diskFolders", id));
        toast({ title: "Папка удалена" });
      } else {
        if (!storagePath) throw new Error("Нет пути к файлу");
        const fileRef = ref(storage, storagePath);
        await deleteObject(fileRef);
        await deleteDoc(doc(db, "diskFiles", id));
        toast({ title: "Файл удалён" });
      }
    } catch (err: any) {
      console.error(err);
      toast({ title: "Ошибка удаления", description: err.message, variant: "destructive" });
    }
  },

  togglePublic: async (id: string, type: "folder" | "file", makePublic: boolean) => {
    try {
      const collectionName = type === "folder" ? "diskFolders" : "diskFiles";
      await updateDoc(doc(db, collectionName, id), { isPublic: makePublic });

      if (type === "file") {
        const file = get().files.find(f => f.id === id);
        if (file) {
          const fileRef = ref(storage, `disk/${file.userId}/${file.path ? file.path + '/' : ''}${file.name}`);
          await updateMetadata(fileRef, {
            customMetadata: { isPublic: makePublic.toString() }
          });
        }
      }

      toast({
        title: makePublic ? "Сделано публичным" : "Сделано приватным"
      });
    } catch (err: any) {
      console.error("Ошибка togglePublic:", err);
      toast({ title: "Ошибка изменения статуса", variant: "destructive" });
    }
  },

  cleanup: () => {
    if (unsubscribeFolders) {
      unsubscribeFolders();
      unsubscribeFolders = null;
    }
    if (unsubscribeFiles) {
      unsubscribeFiles();
      unsubscribeFiles = null;
    }
  },
}));